import React from 'react';

/** @type {HTMLMediaElement | null} */
let lastActiveMedia = null;
/** @type {Set<HTMLMediaElement>} */
const registeredMedia = new Set();
let globalListenerAttached = false;

function isTypingOrControlTarget(target) {
    if (!(target instanceof Element)) return false;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
    if (target instanceof HTMLSelectElement || target instanceof HTMLButtonElement) return true;
    if (target.isContentEditable) return true;
    if (target.closest?.('[contenteditable="true"]')) return true;
    if (target.closest?.('[data-no-shortcuts]')) return true;
    const role = target.getAttribute?.('role');
    if (role === 'combobox' || role === 'option' || role === 'listbox' || role === 'slider') return true;
    return false;
}

function toggleMediaPlayback(media) {
    if (!media?.src) return;
    if (media.paused) {
        media.play().catch(() => {});
    } else {
        media.pause();
    }
}

function onGlobalKeyDown(e) {
    if (e.key !== ' ' || e.repeat) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTypingOrControlTarget(e.target)) return;

    const media = lastActiveMedia;
    if (!media || !registeredMedia.has(media) || !media.src) return;

    // Video/audio elements handle Space natively when focused — avoid double toggling.
    if (document.activeElement === media) return;

    e.preventDefault();
    toggleMediaPlayback(media);
}

function ensureGlobalListener() {
    if (globalListenerAttached) return;
    document.addEventListener('keydown', onGlobalKeyDown, true);
    globalListenerAttached = true;
}

function removeGlobalListenerIfIdle() {
    if (registeredMedia.size > 0 || globalListenerAttached === false) return;
    document.removeEventListener('keydown', onGlobalKeyDown, true);
    globalListenerAttached = false;
}

/**
 * Spacebar play/pause for the last media the user interacted with in this layout.
 * Works after clicking the transcript or player chrome, as long as focus is not
 * in a text field or other control that owns Space.
 */
export default function useMediaSpacebarPlayPause({mediaRef, containerRef, enabled = true, registerKey = ''}) {
    React.useEffect(() => {
        if (!enabled) return undefined;

        const media = mediaRef?.current;
        const container = containerRef?.current;
        if (!media || !container) return undefined;

        const markActive = () => {
            lastActiveMedia = media;
        };

        registeredMedia.add(media);
        ensureGlobalListener();
        markActive();

        const onPointerDown = (event) => {
            if (container.contains(event.target)) {
                markActive();
            }
        };

        container.addEventListener('pointerdown', onPointerDown, true);

        return () => {
            container.removeEventListener('pointerdown', onPointerDown, true);
            registeredMedia.delete(media);
            if (lastActiveMedia === media) {
                lastActiveMedia = registeredMedia.size > 0
                    ? registeredMedia.values().next().value
                    : null;
            }
            removeGlobalListenerIfIdle();
        };
    }, [mediaRef, containerRef, enabled, registerKey]);
}

export {isTypingOrControlTarget, toggleMediaPlayback};
